import React, { useState } from 'react';
import { CameraIcon, PhotoIcon } from '@heroicons/react/24/outline';
import CameraCapture from './camera-capture';
import axios from 'axios';
import { useCreatePersonaSession, useGetCurrentPersonaSession, useUpdatePersonaSession } from '../../hooks/api-hooks';
import LoadingSpinner from '../../shared/spinner';
import { detectFaces } from './utils';
import PhotoPreview from './photo-preview';
import FullscreenCamera from './full-screen-camera';
import useMobileDetect from '../../hooks/use-device-detector';
import PhotoSelectionTrustSignals from './photo-selection-trust-signal';

type CameraState = 'initial' | 'checking' | 'not-available' | 'permission-denied' | 'available';

interface PhotoSelectionProps {
  hasPersona: boolean;
  onGeneratePersonasDone: () => void;
}

const PhotoSelection: React.FC<PhotoSelectionProps> = ({
 onGeneratePersonasDone
}) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [cameraState, setCameraState] = useState<CameraState>('initial');
  const [showCamera, setShowCamera] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isMultiFaceError, setIsMultiFaceError] = useState(false);
  const [isFaceTooClose, setIsFaceTooClose] = useState(false);
  const [isFaceTooFar, setIsFaceTooFar] = useState(false);
  const [isMissingFace, setIsMissingFace] = useState(false);

  const createPersonaSessionMutation = useCreatePersonaSession();
  const updateSessionMutation = useUpdatePersonaSession();
  const session = useGetCurrentPersonaSession();

  const isMobile = useMobileDetect();

  const checkCameraAccess = async () => {
    setCameraState('checking');
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasVideoDevice = devices.some(device => device.kind === 'videoinput');

      if (!hasVideoDevice) {
        setCameraState('not-available');
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        stream.getTracks().forEach(track => track.stop());
        setCameraState('available');
        setShowCamera(true);
      } catch (err) {
        if (err instanceof Error) {
          if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
            setCameraState('permission-denied');
          } else {
            setCameraState('not-available');
          }
        }
      }
    } catch (error) {
      setCameraState('not-available');
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileSelect = async (file: File) => {
    if (!file) return;
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));

    setTimeout(async() => {
      const { numOfFaces, detections } = await detectFaces('preview-target-face');
      if(numOfFaces > 1){
        setIsMultiFaceError(true);
        return;
      } else {
        setIsMultiFaceError(false)
      }

      if(numOfFaces < 1){
        setIsMissingFace(true);
      } else {
        setIsMissingFace(false);
      }

    },0)
  };

  const handleCameraCapture = (file: File) => {
    setShowCamera(false);
    handleFileSelect(file);
  };

  const handleUseCameraInstead = () => {
    setIsMultiFaceError(false);
    setIsMissingFace(false);
    setSelectedFile(null);
    setPreviewUrl(null);
    checkCameraAccess();
  };

  const handleGeneratePersonas = async () => {
    if (!selectedFile) return;

    let newSessionId = null;
    try {
      setIsUploading(true);
      // Create session and get upload URL
      const sessionResponse = await createPersonaSessionMutation.mutateAsync(
        { contentType: selectedFile.type },
      );
      const { id: sessionId, uploadUrl } = sessionResponse;
      newSessionId = sessionId;

      await axios.put(uploadUrl, selectedFile, {
        headers: {
          'Content-Type': selectedFile.type,
          'x-goog-meta-persona-session-id': sessionId.toString(),
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        },
      });
      onGeneratePersonasDone();
    } catch (error) {
      let errorMessage: string;

      if (axios.isAxiosError(error)) {
        errorMessage = `Upload Error: ${error.response?.data?.message ||
        error.message ||
        'Failed to upload file'}`;
      } else if (error instanceof Error) {
        errorMessage = `Upload Error: ${error.message}`;
      } else {
        errorMessage = 'Upload Error: An unknown error occurred';
      }

      if (newSessionId) {
        await updateSessionMutation.mutateAsync({
          sessionId: newSessionId,
          status: 'failed',
          failureReason: errorMessage
        });
      }
    } finally {
      await session.refetch();
      setIsUploading(false);
    }
  };

  const renderInitialView = () => (
    <div className="text-center sm:p-8 max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-gray-100">
        <div className="flex flex-col items-center gap-4">
          <input
            type="file"
            className="sr-only"
            ref={fileInputRef}
            accept="image/*"
            onChange={handleFileInputChange}
          />
          <div className="rounded-full bg-amber-100 p-3">
            <CameraIcon className="h-6 w-6 text-amber-600" />
          </div>
          <div className='space-y-2 w-full sm:w-auto'>
            <h3 className='text-lg font-semibold text-gray-900'>Choose Photo Method</h3>
          </div>
          <div>
            <div className="flex flex-col justify-self-start text-xs text-indigo-600">
              Recommended
            </div>
            <div className='flex flex-col sm:flex-row w-full sm:w-auto gap-3 mt-2'>
              <button
                onClick={checkCameraAccess}
                className='w-full sm:w-auto inline-flex flex-col sm:flex-row justify-center items-center px-6 py-3 text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-sm min-w-[160px]'
              >
                <div className='flex items-center'>
                  <CameraIcon className='h-5 w-5 mr-2' />
                  Use Camera
                </div>
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className='w-full sm:w-auto inline-flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
              >
                <PhotoIcon className='h-4 w-4 mr-2' />
                Upload Photo
              </button>
            </div>
          </div>
        </div>
        <div className='mt-5'>
          <PhotoSelectionTrustSignals />
        </div>
      </div>
    </div>
  );

  const renderPermissionDenied = () => (
    <div className='text-center p-4 sm:p-8 max-w-2xl mx-auto'>
      <div className='bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-gray-100'>
        <div className='flex flex-col items-center gap-4'>
          <div className='rounded-full bg-amber-100 p-3'>
            <CameraIcon className='h-6 w-6 text-amber-600' />
          </div>
          <div className='space-y-2 w-full sm:w-auto'>
            <h3 className='text-lg font-semibold text-gray-900'>Camera Access Required</h3>
            <p className='text-sm text-gray-600 max-w-md mx-auto'>
              Please allow camera access in your browser settings to take a photo.
              {navigator.userAgent.indexOf('Chrome') > -1
                ? ' Look for the camera icon in your address bar.'
                : ' Check your browser settings.'}
            </p>
          </div>
          <div className='flex flex-col sm:flex-row w-full sm:w-auto gap-3 mt-2'>
            <button
              onClick={checkCameraAccess}
              className="w-full sm:w-auto inline-flex justify-center items-center px-6 py-3 text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-sm min-w-[160px]"
            >
              Try Again
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full sm:w-auto inline-flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <PhotoIcon className="h-4 w-4 mr-2" />
              Upload Instead
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (isUploading) {
    return (
      <div className='space-y-4 text-center mt-10'>
        <div className='flex justify-center'>
          <LoadingSpinner className="h-12 w-12 text-indigo-500" />
        </div>
        <p className='text-lg font-semibold'>Uploading...</p>
        <div className='w-full bg-gray-200 rounded-full h-2.5 max-w-md mx-auto'>
          <div
            className='bg-indigo-500 h-2.5 rounded-full transition-all duration-300'
            style={{ width: `${uploadProgress}%` }}
          />
        </div>
      </div>
    );
  }

  if (selectedFile && previewUrl) {
    return (
      <>
        <input
          type='file'
          className='sr-only'
          ref={fileInputRef}
          accept='image/*'
          onChange={handleFileInputChange}
        />
        <PhotoPreview
          previewUrl={previewUrl}
          isMultiFaceError={isMultiFaceError}
          isMissingFace={isMissingFace}
          setIsMissingFace={setIsMissingFace}
          onRetake={handleUseCameraInstead}
          onUpload={() => fileInputRef.current?.click()}
          onGenerate={handleGeneratePersonas}
          isUploading={isUploading}
        />
      </>
    );
  }

  if (cameraState === 'initial') {
    return renderInitialView();
  }

  if (cameraState === 'permission-denied') {
    return renderPermissionDenied();
  }

  if (cameraState === 'checking') {
    return (
      <div className="flex items-center justify-center space-x-2">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
        <span className="text-gray-600">Checking camera availability...</span>
      </div>
    );
  }

  if (showCamera && cameraState === 'available') {
    return (
      <FullscreenCamera
        isOpen={true}
        onClose={() => setShowCamera(false)}
        onUploadInstead={() => fileInputRef.current?.click()}
        isMobile={isMobile}
      >
        <CameraCapture onCapture={handleCameraCapture} />
        <input
          type="file"
          className="sr-only"
          ref={fileInputRef}
          accept="image/*"
          onChange={handleFileInputChange}
        />
      </FullscreenCamera>
    );
  }

  return renderInitialView();
};

export default PhotoSelection;